import { CdkTrapFocus } from "@angular/cdk/a11y";
import { DragDropModule, CdkDragEnd, CdkDragMove } from "@angular/cdk/drag-drop";
import { NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  viewChild,
  inject,
  effect,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

import { FeatureFlag } from "@vowkey/common/enums/feature-flag.enum";
import { ConfigService } from "@vowkey/common/platform/abstractions/config/config.service";
import { I18nPipe } from "@vowkey/ui-common";

import { BitIconButtonComponent } from "../icon-button/icon-button.component";
import { SIDERAIL_WIDTH_REM } from "../shared";

import { NavDividerComponent } from "./nav-divider.component";
import { resizeHandlePercent } from "./side-nav-resize";
import { media, SideNavService } from "./side-nav.service";

export type SideNavVariant = "primary" | "secondary";

/**
 * Side navigation component that provides a collapsible navigation menu.
 */
@Component({
  selector: "bit-side-nav",
  templateUrl: "side-nav.component.html",
  imports: [
    CdkTrapFocus,
    NavDividerComponent,
    BitIconButtonComponent,
    I18nPipe,
    DragDropModule,
    NgTemplateOutlet,
  ],
  host: {
    // Grid placement: always col 1.  In overlay mode the element is also
    // switched to position:fixed so it escapes the grid's stacking context
    // and renders above the scrim (z-40) and the drawer.
    // The 90% cap is a percentage, not 90vw, so it measures the fixed element's
    // containing block: the viewport in the app, the wrapper under Storybook's transform.
    class: "tw-block tw-h-full tw-col-start-1 tw-row-start-1",
    "[class]":
      "sideNavService.isOverlay() ? 'tw-fixed tw-top-0 tw-bottom-0 tw-left-0 tw-z-50 tw-max-w-[90%]' : ''",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent {
  protected readonly sideNavService = inject(SideNavService);

  /**
   * Visual variant of the side navigation
   *
   * @default "primary"
   */
  readonly variant = input<SideNavVariant>("primary");

  private readonly toggleButton = viewChild("toggleButton", { read: ElementRef });

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly configService = inject(ConfigService);

  /**
   * Whether the VFO1 Foundation flag is enabled, which selects the v2 side nav layout.
   */
  private readonly vfo1Enabled = toSignal(
    this.configService.getFeatureFlag$(FeatureFlag.VFO1Foundation),
    { initialValue: false },
  );

  /** Width of the collapsed nav (icon strip / side rail), bound in the template. */
  protected readonly closedWidthRem = SIDERAIL_WIDTH_REM;

  protected readonly isTouchDevice = toSignal(media("(pointer: coarse)"), { initialValue: false });

  private readonly reducedMotion = toSignal(media("(prefers-reduced-motion: reduce)"), {
    initialValue: false,
  });

  /** Handle position announced to assistive tech. The collapsed rail is the low end of the travel. */
  protected readonly widthPercent = computed(() =>
    resizeHandlePercent(
      this.sideNavService.open() ? this.sideNavService.widthRem() : SIDERAIL_WIDTH_REM,
    ),
  );

  /** True when it is safe to animate the nav's width. */
  protected readonly animateWidth = computed(
    () =>
      this.sideNavService.transitionsEnabled() &&
      !this.sideNavService.isDragging() &&
      !this.reducedMotion(),
  );

  protected readonly handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      this.sideNavService.open.set(false);
      this.toggleButton()?.nativeElement.focus();
      return false;
    }

    return true;
  };

  protected onDragMoved(event: CdkDragMove) {
    const rectX = this.elementRef.nativeElement.getBoundingClientRect().x;
    const eventXPointer = event.pointerPosition.x;

    this.sideNavService.setWidthFromDrag(eventXPointer, rectX);

    // Neutralize CDK's accumulated transform to prevent the handle from drifting
    // away from the nav's right edge as the nav width changes.
    event.source.element.nativeElement.style.transform = "none";
  }

  protected onDragEnded(event: CdkDragEnd) {
    this.sideNavService.onDragEnd();
    // Reset CDK's accumulated position so the next drag starts clean,
    // then clear the inline transform so the handle returns to its CSS position.
    event.source.reset();
    event.source.element.nativeElement.style.transform = "none";
  }

  protected onKeydown(event: KeyboardEvent) {
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      this.sideNavService.setWidthFromKeys(event.key);
    }
  }

  constructor() {
    effect(() => {
      this.sideNavService.version.set(this.vfo1Enabled() ? "vfo1" : "default");
    });
  }
}
